/**
 * Шапка рабочего места макета-эталона: хлебные крошки, помощь по странице,
 * переход в AI Copilot и дата. Copilot в макете виден всегда: кнопка есть у любой роли,
 * без права copilot.use раздел открывается только для чтения (решение владельца 25.10.2026).
 * «Помощь» сразу запускает урок раздела (М/training.js:365); как меню и страница обучения --
 * только с правом training.use. До 700 px правая часть шапки скрыта правилом макета.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { contextLesson } from '@/features/training/contextLesson';
import { useTrainingTour } from '@/features/training/useTrainingTour';
import { useFormatters } from '@/hooks/useFormatters';
import { useNow } from '@/hooks/useNow';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeaturesStore } from '@/stores/features';
import { useShellBreadcrumbs } from './breadcrumbs';

/** Как часто сверяется дата шапки: смена суток видна не позже чем через минуту */
const DATE_REFRESH_MS = 60_000;

export function Topbar() {
	const { t } = useTranslation('common');
	const { t: tNav } = useTranslation('nav');
	const { pathname } = useLocation();
	const copilotEnabled = useFeaturesStore((s) => s.isEnabled('aiCopilot'));
	const canTrain = usePermissions().has('training.use');
	const { startLesson } = useTrainingTour();
	/* Сутки организации, как в остальных датах интерфейса, а не сутки браузера */
	const { formatLongDate } = useFormatters();
	const now = useNow(DATE_REFRESH_MS);
	const crumbs = useShellBreadcrumbs();

	return (
		<header className="ws-topbar">
			<nav className="ws-breadcrumbs" aria-label={t('shell.breadcrumbs')}>
				<WorkspaceIcon name="overview" />
				{crumbs.map((crumb, index) => (
					<Fragment key={index}>
						{index > 0 && <WorkspaceIcon name="chevron" />}
						{crumb.to === undefined ? (
							<span className="ws-current" aria-current="page">
								{crumb.label}
							</span>
						) : (
							<Link to={crumb.to}>{crumb.label}</Link>
						)}
					</Fragment>
				))}
			</nav>
			<div className="ws-topbar-right">
				{canTrain && (
					<button
						type="button"
						className="ws-training-help"
						aria-label={t('shell.helpLabel')}
						onClick={() => void startLesson(contextLesson(pathname))}
					>
						<WorkspaceIcon name="book" />
						<span>{t('shell.help')}</span>
					</button>
				)}
				{copilotEnabled && (
					<Link className="ws-copilot-shortcut" to="/ai-copilot">
						<WorkspaceIcon name="ai" />
						<span>{tNav('copilot')}</span>
					</Link>
				)}
				<span className="ws-topbar-divider" />
				<span className="ws-date-label">{formatLongDate(now)}</span>
			</div>
		</header>
	);
}
